import { buildFieldDefsFromDefinitions } from '@/utils/leadFields'
import {
  dateInputValueToEpoch,
  epochToDateInputValue,
  normalizeEpochMs,
} from '@/utils/leadFieldTypes'

/**
 * Filter operators for createTask / editTask.
 * Same field → OR across entries (or use IN). Different fields → AND.
 */
export const CAMPAIGN_FILTER_OPERATORS = [
  { value: 'EQUALS', title: 'equals', arity: 1, kinds: ['text', 'number', 'date'] },
  { value: 'IN', title: 'is one of', arity: 'many', kinds: ['text', 'number'] },
  { value: 'BETWEEN', title: 'between', arity: 2, kinds: ['number', 'date'] },
  { value: 'GREATER_THAN', title: 'greater than', arity: 1, kinds: ['number'] },
  { value: 'LESS_THAN', title: 'less than', arity: 1, kinds: ['number'] },
  { value: 'AFTER', title: 'after', arity: 1, kinds: ['date'] },
  { value: 'BEFORE', title: 'before', arity: 1, kinds: ['date'] },
]

/** Baseline lead columns always available for filtering. */
export const STANDARD_CAMPAIGN_FILTER_FIELDS = [
  { key: 'contact_name', label: 'Contact name', type: 'TEXT', options: [] },
  { key: 'contact_email', label: 'Contact email', type: 'TEXT', options: [] },
  { key: 'contact_number', label: 'Contact number', type: 'PHONE_NUMBER', options: [] },
  { key: 'type_of_business', label: 'Type of business', type: 'TEXT', options: [] },
  { key: 'industry', label: 'Industry', type: 'TEXT', options: [] },
  { key: 'request_status', label: 'Status', type: 'TEXT', options: [] },
  { key: 'lead_score', label: 'Lead score', type: 'NUMBER', options: [] },
  { key: 'created_on', label: 'Created on', type: 'DATE', options: [] },
  { key: 'updated_on', label: 'Updated on', type: 'DATE', options: [] },
]

let filterRowCounter = 0

export function createEmptyCampaignFilter() {
  filterRowCounter += 1
  return {
    id: `filter-${filterRowCounter}`,
    field: '',
    operator: 'EQUALS',
    values: [],
  }
}

/** Rebuild UI filter rows from a campaign's stored filters. */
export function hydrateCampaignFiltersFromApi(filters = []) {
  if (!Array.isArray(filters) || !filters.length) return []
  return filters.map((row) => {
    const empty = createEmptyCampaignFilter()
    const operator = String(row?.operator ?? 'EQUALS').trim().toUpperCase() || 'EQUALS'
    const values = Array.isArray(row?.values) ? [...row.values] : []
    return {
      ...empty,
      field: String(row?.field ?? '').trim(),
      operator,
      values,
    }
  })
}

/** Map product field type → filter kind used for operator filtering. */
export function campaignFilterFieldKind(fieldDef) {
  const type = String(fieldDef?.type ?? 'TEXT').trim().toUpperCase()
  if (type === 'DATE' || type === 'DATETIME' || type === 'TIMESTAMP') return 'date'
  if (type === 'NUMBER' || type === 'INTEGER' || type === 'FLOAT') return 'number'
  const key = String(fieldDef?.key ?? '').trim().toLowerCase()
  if (key === 'created_on' || key === 'updated_on' || key.endsWith('_on') || key.endsWith('_at')) {
    return 'date'
  }
  if (key === 'lead_score') return 'number'
  return 'text'
}

const SELECT_FIELD_TYPES = new Set([
  'DROPDOWN',
  'SELECT',
  'RADIO',
  'ENUM',
  'MULTI_SELECT',
  'MULTISELECT',
])

const SELECT_FIELD_KEYS = new Set([
  'industry',
  'type_of_business',
  'request_status',
  'source',
])

/** Fields that should use a value dropdown in advanced filters. */
export function isSelectFilterField(fieldDef) {
  if (!fieldDef) return false
  const type = String(fieldDef.type ?? '').trim().toUpperCase()
  if (SELECT_FIELD_TYPES.has(type)) return true
  if (Array.isArray(fieldDef.options) && fieldDef.options.length) return true
  const key = String(fieldDef.key ?? '').trim().toLowerCase()
  return SELECT_FIELD_KEYS.has(key)
}

/**
 * Distinct value options for a filter field from definition options + lead data.
 */
export function buildFilterValueOptions(fieldDef, leads = []) {
  const seen = new Set()
  const options = []

  function push(raw) {
    const text = String(raw ?? '').trim()
    if (!text || text === '—') return
    const key = text.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    options.push(text)
  }

  for (const opt of Array.isArray(fieldDef?.options) ? fieldDef.options : []) {
    if (opt && typeof opt === 'object') {
      push(opt.value ?? opt.title ?? opt.label ?? opt.key)
    } else {
      push(opt)
    }
  }

  for (const lead of Array.isArray(leads) ? leads : []) {
    push(resolveLeadFilterValue(lead, fieldDef?.key))
  }

  return options.sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }),
  )
}

export function operatorsForFilterField(fieldDef) {
  const kind = campaignFilterFieldKind(fieldDef)
  return CAMPAIGN_FILTER_OPERATORS.filter((op) => op.kinds.includes(kind))
}

export function filterOperatorMeta(operator) {
  const key = String(operator ?? '').trim().toUpperCase()
  return CAMPAIGN_FILTER_OPERATORS.find((op) => op.value === key) || null
}

/** Union of filterable fields from the selected products' definitions. */
export function buildCampaignFilterFieldOptions(contexts = []) {
  const map = new Map()

  for (const field of STANDARD_CAMPAIGN_FILTER_FIELDS) {
    map.set(field.key, { ...field })
  }

  const list = Array.isArray(contexts) ? contexts : []

  for (const context of list) {
    const defs = buildFieldDefsFromDefinitions(
      context?.custom_field_definitions ?? context?.customFieldDefinitions ?? {},
    )
    for (const def of defs) {
      if (!def?.key) continue
      const existing = map.get(def.key)
      if (!existing) {
        map.set(def.key, { ...def })
        continue
      }
      if (def.type === 'DROPDOWN' && Array.isArray(def.options) && def.options.length) {
        existing.options = [
          ...new Set([...(existing.options || []), ...def.options]),
        ]
      }
      if (def.label && (!existing.label || existing.label === existing.key)) {
        existing.label = def.label
      }
    }
  }

  return [...map.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }),
  )
}

/** Fields that may store an assigned agent email (for FIELD_BASED suggestions). */
export function buildAgentFieldSuggestions(fieldOptions = []) {
  const list = Array.isArray(fieldOptions) ? fieldOptions : []
  const scored = list.map((field) => {
    const key = String(field.key ?? '').toLowerCase()
    const label = String(field.label ?? '').toLowerCase()
    let score = 0
    if (key.includes('agent') && key.includes('email')) score += 4
    else if (key.includes('agent')) score += 2
    else if (key.includes('email')) score += 2
    if (label.includes('agent') && label.includes('email')) score += 3
    else if (label.includes('assigned')) score += 1
    return { field, score }
  })

  return scored
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.field.label.localeCompare(b.field.label))
    .map((row) => row.field.key)
}

function resolveFilterFieldKey(field) {
  if (field && typeof field === 'object') {
    return String(field.key ?? field.value ?? '').trim()
  }
  return String(field ?? '').trim()
}

/** Convert a UI value to API value (string | number epoch-ms). */
function coerceFilterApiValue(raw, { kind, operator }) {
  if (raw == null) return null
  if (typeof raw === 'object') {
    const nested = raw.value ?? raw.key ?? raw.title
    return coerceFilterApiValue(nested, { kind, operator })
  }

  const op = String(operator ?? '').toUpperCase()
  const text = String(raw).trim()
  if (!text && raw !== 0) return null

  if (kind === 'date' || ['AFTER', 'BEFORE'].includes(op)) {
    // Prefer YYYY-MM-DD from date inputs; also accept epoch / display dates.
    const fromInput = dateInputValueToEpoch(text)
    if (fromInput != null) return fromInput
    const epoch = normalizeEpochMs(raw)
    return epoch
  }

  if (
    kind === 'number' ||
    ['GREATER_THAN', 'LESS_THAN'].includes(op) ||
    (op === 'BETWEEN' && kind === 'number')
  ) {
    const num = Number(text)
    return Number.isFinite(num) ? num : null
  }

  // BETWEEN on dates handled above via kind === 'date'
  if (op === 'BETWEEN') {
    const fromInput = dateInputValueToEpoch(text)
    if (fromInput != null) return fromInput
    const epoch = normalizeEpochMs(raw)
    if (epoch != null) return epoch
    const num = Number(text)
    return Number.isFinite(num) ? num : null
  }

  return text
}

/**
 * Normalize UI filter rows → createTask / editTask filters payload.
 * values is always an array; dates/numbers are plain numbers (epoch-ms for dates).
 */
export function normalizeCampaignFiltersForApi(filters = [], fieldOptions = []) {
  if (!Array.isArray(filters)) return []

  return filters
    .map((row) => {
      const field = resolveFilterFieldKey(row?.field)
      const operator = String(row?.operator ?? 'EQUALS').trim().toUpperCase()
      const def = findCampaignFilterFieldDef(fieldOptions, field)
      const kind = campaignFilterFieldKind(def)
      const meta = filterOperatorMeta(operator)

      const rawValues = Array.isArray(row?.values) ? row.values : []
      const values = rawValues
        .map((value) => coerceFilterApiValue(value, { kind, operator }))
        .filter((value) => value != null && value !== '')

      if (!field || !operator || !values.length) return null
      if (meta?.arity === 2 && values.length < 2) return null
      if (meta?.arity === 1 && values.length > 1) {
        return { field, operator, values: [values[0]] }
      }
      if (meta?.arity === 2) {
        return { field, operator, values: [values[0], values[1]] }
      }
      return { field, operator, values }
    })
    .filter(Boolean)
}

export function findCampaignFilterFieldDef(fieldOptions, fieldKey) {
  const key = String(fieldKey ?? '').trim()
  if (!key) return null
  const fromOptions = (Array.isArray(fieldOptions) ? fieldOptions : []).find(
    (field) => String(field.key ?? '').trim() === key,
  )
  if (fromOptions) return fromOptions
  return {
    key,
    label: key.replace(/_/g, ' '),
    type: 'TEXT',
    options: [],
  }
}

/** Read/write helpers for filter value slots in the create dialog. */
export function getFilterSingleValue(filter) {
  const values = Array.isArray(filter?.values) ? filter.values : []
  return values[0] ?? ''
}

export function setFilterSingleValue(filter, value) {
  let next = value
  if (next && typeof next === 'object') {
    next = next.value ?? next.key ?? next.title ?? ''
  }
  filter.values = next === '' || next == null ? [] : [next]
}

export function getFilterRangeMin(filter) {
  const values = Array.isArray(filter?.values) ? filter.values : []
  return values[0] ?? ''
}

export function getFilterRangeMax(filter) {
  const values = Array.isArray(filter?.values) ? filter.values : []
  return values[1] ?? ''
}

export function setFilterRangeMin(filter, value) {
  const max = getFilterRangeMax(filter)
  filter.values = [value ?? '', max]
}

export function setFilterRangeMax(filter, value) {
  const min = getFilterRangeMin(filter)
  filter.values = [min, value ?? '']
}

/** For date inputs bound to epoch-capable filter slots. */
export function getFilterDateInputValue(value) {
  if (value == null || value === '') return ''
  const asInput = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(asInput)) return asInput
  return epochToDateInputValue(value)
}

/** Resolve a filter field value from a normalized campaign/lead record. */
export function resolveLeadFilterValue(lead, fieldKey) {
  const key = String(fieldKey ?? '').trim()
  if (!key || !lead) return null

  const standard = {
    contact_name: lead.name,
    contact_email: lead.email === '—' ? '' : lead.email,
    contact_number: lead.phoneRaw || lead.phone,
    type_of_business: lead.typeOfBusiness,
    industry: lead.industry,
    request_status: lead.status,
    lead_score: lead.leadScore,
    created_on: lead.createdOn,
    updated_on: lead.updatedOn ?? lead.raw?.updated_on ?? lead.raw?.updatedOn,
  }
  if (Object.prototype.hasOwnProperty.call(standard, key)) {
    return standard[key] ?? null
  }

  const custom = lead.customFields
  if (custom && typeof custom === 'object' && custom[key] != null) {
    return custom[key]
  }

  const raw = lead.raw
  if (raw && typeof raw === 'object' && raw[key] != null) return raw[key]
  return null
}

function coerceComparable(value, kind) {
  if (value == null || value === '') return null
  if (kind === 'number') {
    const num = Number(value)
    return Number.isFinite(num) ? num : null
  }
  if (kind === 'date') {
    return normalizeEpochMs(value)
  }
  return String(value).trim().toLowerCase()
}

function valueMatchesOperator(leadValue, operator, values, kind) {
  const op = String(operator ?? '').toUpperCase()
  const expected = (Array.isArray(values) ? values : [])
    .map((value) => coerceComparable(value, kind))
    .filter((value) => value != null && value !== '')

  if (!expected.length) return true

  const actual = coerceComparable(leadValue, kind)
  if (actual == null || actual === '') return false

  if (op === 'IN') {
    return expected.some((value) => value === actual)
  }
  if (op === 'EQUALS') {
    return actual === expected[0]
  }
  if (op === 'GREATER_THAN' || op === 'AFTER') {
    return actual > expected[0]
  }
  if (op === 'LESS_THAN' || op === 'BEFORE') {
    return actual < expected[0]
  }
  if (op === 'BETWEEN' && expected.length >= 2) {
    const lo = Math.min(expected[0], expected[1])
    const hi = Math.max(expected[0], expected[1])
    return actual >= lo && actual <= hi
  }
  return actual === expected[0]
}

/**
 * Client-side match for bulk-assign advanced filters.
 * Same field → OR across rows; different fields → AND.
 */
export function leadMatchesCampaignFilters(
  lead,
  filters = [],
  fieldOptions = [],
) {
  const normalized = normalizeCampaignFiltersForApi(filters, fieldOptions)
  if (!normalized.length) return true

  const byField = new Map()
  for (const row of normalized) {
    const list = byField.get(row.field) || []
    list.push(row)
    byField.set(row.field, list)
  }

  for (const [field, rows] of byField) {
    const def = findCampaignFilterFieldDef(fieldOptions, field)
    const kind = campaignFilterFieldKind(def)
    const leadValue = resolveLeadFilterValue(lead, field)
    const matched = rows.some((row) =>
      valueMatchesOperator(leadValue, row.operator, row.values, kind),
    )
    if (!matched) return false
  }
  return true
}
